import { useState, useEffect } from 'react';
import { Activity, Mic, MessageCircle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { asPlainText, shouldShowBonitaFeedPost } from '@/lib/safeText';


// Theme hook for components
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute('data-theme') === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
};

// Time ago helper
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = (now - date) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// "The Pulse" — Community sidebar widget
export const TrendingWidget = ({ className }) => {
  const [bonitaPost, setBonitaPost] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isDark = useTheme();

  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const textMutedClass = isDark ? 'text-white/40' : 'text-gray-500';
  const borderClass = isDark ? 'border-white/10' : 'border-gray-200';

  useEffect(() => {
    fetchPulseData();
  }, []);

  const fetchPulseData = async () => {
    try {
      const [postRes, statsRes] = await Promise.allSettled([
        api.get('/posts/user/bonita?limit=15', { withCredentials: true }),
        api.get('/stats/pulse', { withCredentials: true }),
      ]);

      if (postRes.status === 'fulfilled') {
        const posts = postRes.value.data;
        if (Array.isArray(posts)) {
          const good = posts.find((p) => shouldShowBonitaFeedPost(p));
          if (good) setBonitaPost(good);
        }
      }
      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data);
      }
    } catch (err) {
      console.error('Pulse fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={className}>
        <Skeleton className="h-4 w-24 mb-4" />
        <Skeleton className="h-16 w-full mb-6" />
        <Skeleton className="h-4 w-20 mb-3" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Section 1: Bonita Says */}
      <div className="mb-6">
        <h3 className={cn("font-display text-xs tracking-widest uppercase mb-3", textMutedClass)}>
          Bonita Says
        </h3>
        {bonitaPost ? (
          <div className={cn("p-3 rounded-lg border", isDark ? 'border-amber-500/20 bg-amber-500/5' : 'border-amber-300/40 bg-amber-50')}>
            <p className={cn("text-sm leading-relaxed", textClass)}>
              &ldquo;{(() => {
                const t = asPlainText(bonitaPost.content);
                return t.length > 200 ? `${t.slice(0, 200)}...` : t;
              })()}&rdquo;
            </p>
            <p className={cn("text-[10px] mt-2", textMutedClass)}>
              {timeAgo(bonitaPost.created_at)}
            </p>
          </div>
        ) : (
          <p className={cn("text-xs", textMutedClass)}>Bonita's cooking something up...</p>
        )}
      </div>

      {/* Section 2: The Pulse */}
      <div className={cn("mb-6 pb-6 border-b", borderClass)}>
        <div className="flex items-center gap-1.5 mb-3">
          <Activity className={cn("h-3.5 w-3.5", textMutedClass)} />
          <h3 className={cn("font-display text-xs tracking-widest uppercase", textMutedClass)}>
            The Pulse
          </h3>
        </div>
        <div className="space-y-2.5">
          <div className="flex justify-between items-center">
            <span className={cn("text-xs", textMutedClass)}>On The Block</span>
            <span className={cn("text-xs font-medium", textClass)}>{stats?.active_today ?? '--'} today</span>
          </div>
          <div className="flex justify-between items-center">
            <span className={cn("text-xs", textMutedClass)}>Posts Dropped</span>
            <span className={cn("text-xs font-medium", textClass)}>{stats?.posts_today ?? '--'}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className={cn("text-xs", textMutedClass)}>Community</span>
            <span className={cn("text-xs font-medium", textClass)}>{stats?.total_users ?? '--'} members</span>
          </div>
        </div>
      </div>

      {/* Section 3: Pull Up */}
      <div>
        <h3 className={cn("font-display text-xs tracking-widest uppercase mb-3", textMutedClass)}>
          Pull Up
        </h3>
        <div className="space-y-2">
          <Link
            to="/stoop"
            className={cn("flex items-center gap-2 text-xs transition-opacity", textMutedClass, "hover:opacity-100 opacity-60")}
          >
            <Mic className="h-3.5 w-3.5" />
            The Stoop
          </Link>
          <Link
            to="/gc"
            className={cn("flex items-center gap-2 text-xs transition-opacity", textMutedClass, "hover:opacity-100 opacity-60")}
          >
            <MessageCircle className="h-3.5 w-3.5" />
            The GC
          </Link>
        </div>
      </div>
    </div>
  );
};

// Rich Link Preview Card for posts with reference URLs
export const LinkPreviewCard = ({ url, className }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const isDark = useTheme();
  
  // Theme-aware classes
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const textMutedClass = isDark ? 'text-white/50' : 'text-gray-600';
  const textVeryMutedClass = isDark ? 'text-white/40' : 'text-gray-500';
  const borderClass = isDark ? 'border-white/20' : 'border-gray-200';
  const borderHoverClass = isDark ? 'hover:border-white/40' : 'hover:border-gray-400';
  const bgClass = isDark ? 'bg-white/5' : 'bg-gray-50';

  useEffect(() => {
    if (url) {
      fetchPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const fetchPreview = async () => {
    try {
      const response = await api.get(`/link-preview`, {
        params: { url },
        withCredentials: true
      });
      setPreview(response.data);
    } catch (error) {
      console.error('Error fetching link preview:', error);
      // Fallback: extract domain from URL
      try {
        const urlObj = new URL(url);
        setPreview({
          title: urlObj.pathname.split('/').filter(Boolean).pop() || 'Link',
          description: 'View the full article',
          domain: urlObj.hostname.replace('www.', ''),
          image: null,
          url: url
        });
      } catch {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!url || error) return null;

  if (loading) {
    return (
      <div className={cn("border overflow-hidden", borderClass, className)}>
        <Skeleton className="h-32 w-full" />
        <div className="p-3">
          <Skeleton className="h-4 w-3/4 mb-2" />
          <Skeleton className="h-3 w-1/4" />
        </div>
      </div>
    );
  }

  // Check if it's a Google Search URL
  const isGoogleSearch = url.includes('google.com/search');

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn("block border overflow-hidden transition-colors", borderClass, borderHoverClass, className)}
      data-testid="link-preview"
    >
      {/* Image */}
      {preview?.image && (
        <div className={cn(
          "overflow-hidden",
          bgClass,
          isGoogleSearch ? "h-16 flex items-center justify-center" : "h-32"
        )}>
          <img
            src={preview.image}
            alt=""
            className={cn(
              "object-contain",
              isGoogleSearch ? "h-8" : "w-full h-full object-cover"
            )}
            onError={(e) => e.target.style.display = 'none'}
          />
        </div>
      )}
      
      {/* Content */}
      <div className={cn("p-3", bgClass)}>
        <p className={cn("text-sm font-medium line-clamp-2 mb-1", textClass)}>
          {preview?.title || 'Link'}
        </p>
        {preview?.description && (
          <p className={cn("text-xs line-clamp-2 mb-2", textMutedClass)}>
            {preview.description}
          </p>
        )}
        <div className={cn("flex items-center gap-1.5 text-[10px]", textVeryMutedClass)}>
          <ExternalLink className="h-3 w-3" />
          <span>{preview?.domain || new URL(url).hostname}</span>
        </div>
      </div>
    </a>
  );
};
